import type { Video, VideoFilterResult, FilteredVideo } from '../types/video.js';
import type { ListEntry } from '../types/settings.js';
import { isLanguageAllowed, detectLanguageFromText, LANGUAGE_CONFIDENCE_THRESHOLD } from './language-filter.js';
import { parseIso8601DurationSeconds } from '../video/duration.js';

export interface VideoFilterOptions {
    allowlist: ListEntry[];
    denylist: ListEntry[];
    requireMadeForKids: boolean;
    regionCode?: string;
    blockShortVideos?: boolean;
    minimumDurationSeconds?: number;
}

export const MINIMUM_VIDEO_DURATION_SECONDS = 5 * 60;

export const DEFAULT_FILTER_OPTIONS: VideoFilterOptions = {
    allowlist: [],
    denylist: [],
    requireMadeForKids: true,
    blockShortVideos: true,
    minimumDurationSeconds: MINIMUM_VIDEO_DURATION_SECONDS,
};

function isInList(list: ListEntry[], videoId: string, channelId: string): ListEntry | undefined {
    return list.find(
        (entry) =>
            (entry.type === 'video' && entry.id === videoId) ||
            (entry.type === 'channel' && entry.id === channelId)
    );
}

/**
 * Detect if a video is shorter than the configured minimum duration.
 * Duration is in ISO 8601 format: PT5M30S, PT45S, etc.
 */
function isUnderMinimumDuration(video: Video, minimumDurationSeconds: number): boolean {
    const totalSeconds = parseIso8601DurationSeconds(video.contentDetails?.duration);
    return totalSeconds > 0 && totalSeconds < minimumDurationSeconds;
}

/**
 * Check video language against allowed languages.
 * Returns detection result with language info.
 */
function checkVideoLanguage(video: Video): VideoFilterResult | null {
    const audioLanguage = video.snippet.defaultAudioLanguage;
    const defaultLanguage = video.snippet.defaultLanguage;

    if (isLanguageAllowed(audioLanguage)) {
        return { allowed: true, detectedLanguage: audioLanguage!.toLowerCase().split('-')[0], languageConfidence: 1.0 };
    }

    if (isLanguageAllowed(defaultLanguage)) {
        return { allowed: true, detectedLanguage: defaultLanguage!.toLowerCase().split('-')[0], languageConfidence: 0.95 };
    }

    const textDetection = detectLanguageFromText(video.snippet.title, video.snippet.description);
    if (textDetection.isAllowed && textDetection.confidence >= LANGUAGE_CONFIDENCE_THRESHOLD) {
        return { allowed: true, detectedLanguage: textDetection.language ?? undefined, languageConfidence: textDetection.confidence };
    }

    if (textDetection.language) {
        return {
            allowed: false,
            reason: textDetection.confidence < LANGUAGE_CONFIDENCE_THRESHOLD ? 'language_uncertain' : 'language_not_allowed',
            detectedLanguage: textDetection.language,
            languageConfidence: textDetection.confidence,
        };
    }

    return { allowed: false, reason: 'language_not_allowed' };
}

function normalizeRegionCode(regionCode?: string): string | null {
    if (!regionCode) return null;
    const normalized = regionCode.trim().toUpperCase();
    return /^[A-Z]{2}$/.test(normalized) ? normalized : null;
}

function isRegionBlocked(video: Video, regionCode?: string): boolean {
    const country = normalizeRegionCode(regionCode);
    const restriction = video.contentDetails?.regionRestriction;
    if (!country || !restriction) return false;

    const blocked = restriction.blocked?.map(code => code.toUpperCase()) || [];
    if (blocked.includes(country)) return true;

    const allowed = restriction.allowed?.map(code => code.toUpperCase()) || [];
    return allowed.length > 0 && !allowed.includes(country);
}

export function filterVideo(video: Video, options: VideoFilterOptions = DEFAULT_FILTER_OPTIONS): VideoFilterResult {
    const {
        allowlist,
        denylist,
        requireMadeForKids,
        regionCode,
        blockShortVideos = true,
        minimumDurationSeconds = MINIMUM_VIDEO_DURATION_SECONDS
    } = options;
    const videoId = video.id;
    const channelId = video.snippet.channelId;

    // 1. MINIMUM DURATION CHECK - Filter out videos shorter than 5 minutes by default.
    if (blockShortVideos && isUnderMinimumDuration(video, minimumDurationSeconds)) {
        return { allowed: false, reason: 'is_short' };
    }

    // 2. PLAYABILITY CHECK - Do not show thumbnails for videos that cannot be embedded or watched locally.
    if (video.status?.embeddable === false) {
        return { allowed: false, reason: 'not_embeddable' };
    }

    if (isRegionBlocked(video, regionCode)) {
        return { allowed: false, reason: 'region_blocked' };
    }

    // 3. DENYLIST CHECK - Always block denylisted content
    const denyEntry = isInList(denylist, videoId, channelId);
    if (denyEntry) {
        return { allowed: false, reason: denyEntry.type === 'video' ? 'denylisted_video' : 'denylisted_channel' };
    }

    // 4. LANGUAGE CHECK - Applies to ALL videos, including allowlisted channels
    // This ensures only Turkish/Dutch videos are shown, even from liked channels
    const languageResult = checkVideoLanguage(video);
    if (languageResult && !languageResult.allowed) {
        return languageResult;
    }

    // 5. ALLOWLIST CHECK - If allowlisted, skip madeForKids check
    const allowEntry = isInList(allowlist, videoId, channelId);
    const isAllowlisted = !!allowEntry;

    if (isAllowlisted) {
        // Return with language info if we detected it
        return languageResult || { allowed: true };
    }

    // 6. MADE FOR KIDS CHECK - Non-allowlisted videos must be madeForKids
    if (requireMadeForKids) {
        const madeForKids = video.status?.madeForKids;
        if (madeForKids !== true) {
            return { allowed: false, reason: 'not_made_for_kids' };
        }
    }

    // Video passed all checks
    return languageResult || { allowed: true };
}

export function filterVideos(videos: Video[], options: VideoFilterOptions = DEFAULT_FILTER_OPTIONS): Video[] {
    return videos.filter((video) => filterVideo(video, options).allowed);
}

export function filterVideosWithResults(videos: Video[], options: VideoFilterOptions = DEFAULT_FILTER_OPTIONS): FilteredVideo[] {
    return videos.map((video) => ({ ...video, filterResult: filterVideo(video, options) }));
}
